/**
 * Poppedijn Verlanglijstje v2
 * --------------------------------------------------
 * Stores wishlist in localStorage.
 * Supports sharing as named lists (verjaardag / geboorte).
 * Multiple shared lists can coexist, each with own claims.
 *
 * SHARED CLAIMS (Poppedijn API on Railway)
 * =========================================
 * Reservations are stored in a shared PostgreSQL database.
 * Each claim is tied to an anonymous visitor_id so only
 * the original claimer can unclaim a product.
 */

(function () {
  'use strict';

  /* Guard tegen dubbel laden: theme.liquid (lazy) en wishlist-page sectie
     kunnen allebei dit script injecteren; de tweede keer is een no-op. */
  if (window.__poppedijnWishlistLoaded) return;
  window.__poppedijnWishlistLoaded = true;

  /* ── Poppedijn API (Railway) ────────────────────── */
  /* Bron: window.POPPEDIJN_API_BASE uit theme.liquid (configureerbaar via shop.metafields.custom.poppedijn_api_url) */
  /* AVG-afweging (2026-06-10): alle calls naar deze API zijn functioneel-noodzakelijk
     voor een door de bezoeker expliciet gevraagde dienst (lijst delen, gedeelde lijst
     bekijken, claimen). Er wordt niet getrackt of geprofileerd; geen consent-gate nodig.
     Wel vereist: vermelding van deze verwerking in de privacyverklaring. */
  var CLAIMS_API_URL = (typeof window !== 'undefined' && window.POPPEDIJN_API_BASE) || 'https://poppedijn-api-production.up.railway.app';
  var API_TIMEOUT_MS = 8000;

  /* Fetch met timeout - voorkomt eindeloze laad-states bij hangende API */
  function apiFetch(url, opts) {
    return Promise.race([
      fetch(url, opts || {}),
      new Promise(function (_, reject) {
        setTimeout(function () { reject(new Error('TIMEOUT')); }, API_TIMEOUT_MS);
      })
    ]);
  }

  /* ── Safe storage wrappers - private mode (Safari) blokkeert setItem ── */
  /* In-memory fallback zodat wishlist nog werkt binnen de pagina-sessie */
  var _memoryStore = {};
  function safeSet(key, value) {
    try {
      localStorage.setItem(key, value);
    } catch (e) {
      _memoryStore[key] = value;
    }
  }
  function safeGet(key) {
    try {
      var v = localStorage.getItem(key);
      if (v !== null) return v;
    } catch (e) {}
    return _memoryStore[key] || null;
  }
  function safeRemove(key) {
    try { localStorage.removeItem(key); } catch (e) {}
    delete _memoryStore[key];
  }

  /* ── Section settings (passed from Liquid via window.__wishlistConfig) ── */
  var CFG = window.__wishlistConfig || {};

  function tpl(str, vars) {
    if (!str) return '';
    Object.keys(vars).forEach(function (k) {
      str = str.split('{' + k + '}').join(vars[k]);
    });
    return str;
  }

  /* HTML escape helper to prevent XSS */
  function escHtml(str) {
    var d = document.createElement('div');
    d.textContent = str;
    return d.innerHTML;
  }

  var STORAGE_KEY = 'poppedijn_wishlist';
  var MY_LISTS_KEY = 'poppedijn_my_lists';
  var SHARED_LISTS_KEY = 'poppedijn_shared_lists';
  var VISITOR_KEY = 'poppedijn_visitor_id';
  var EDIT_STATE_KEY = 'poppedijn_wishlist_edit';
  var _claimsPollTimer = null;

  function getEditState() {
    try { return JSON.parse(localStorage.getItem(EDIT_STATE_KEY)); }
    catch (e) { return null; }
  }

  function setEditState(state) {
    if (state) {
      safeSet(EDIT_STATE_KEY, JSON.stringify(state));
    } else {
      safeRemove(EDIT_STATE_KEY);
    }
  }

  /* ── Anonymous visitor ID (for claim ownership) ─── */

  function getVisitorId() {
    var id = localStorage.getItem(VISITOR_KEY);
    if (!id) {
      id = 'v-' + Math.random().toString(36).substr(2, 9) + '-' + Date.now().toString(36);
      safeSet(VISITOR_KEY, id);
    }
    return id;
  }

  /* ── Helpers ────────────────────────────────── */

  function getWishlist() {
    try { return JSON.parse(localStorage.getItem(STORAGE_KEY)) || []; }
    catch (e) { return []; }
  }

  function saveWishlist(items) {
    safeSet(STORAGE_KEY, JSON.stringify(items));
    updateAllButtons();
    updateCountBadges();
  }

  function isInWishlist(productId) {
    return getWishlist().some(function (item) { return String(item.id) === String(productId); });
  }

  function addToWishlist(product) {
    var items = getWishlist();
    if (items.some(function (i) { return String(i.id) === String(product.id); })) return;
    items.push(Object.assign({}, product, { addedAt: Date.now() }));
    saveWishlist(items);
    showToast(product.title + ' toegevoegd aan je verlanglijstje ❤️');
  }

  function removeFromWishlist(productId) {
    var items = getWishlist().filter(function (i) { return String(i.id) !== String(productId); });
    saveWishlist(items);
  }

  function toggleWishlist(productId, productData) {
    if (isInWishlist(productId)) {
      removeFromWishlist(productId);
    } else {
      addToWishlist(productData);
    }
  }

  /* ── Toast notification ────────────────────── */

  function showToast(message) {
    var existing = document.querySelector('.wishlist-toast');
    if (existing) existing.remove();

    var toast = document.createElement('div');
    toast.className = 'wishlist-toast';
    toast.textContent = message;
    document.body.appendChild(toast);

    requestAnimationFrame(function () {
      toast.classList.add('is-visible');
    });

    setTimeout(function () {
      toast.classList.remove('is-visible');
      setTimeout(function () { toast.remove(); }, 300);
    }, 2500);
  }

  /* ── Update UI ─────────────────────────────── */

  function updateAllButtons() {
    document.querySelectorAll('[data-wishlist-toggle]').forEach(function (btn) {
      var id = btn.dataset.wishlistToggle;
      btn.classList.toggle('is-wishlisted', isInWishlist(id));
      var label = btn.querySelector('span');
      if (label) {
        label.textContent = isInWishlist(id) ? 'Op je verlanglijstje' : 'Toevoegen aan verlanglijst';
      }
    });
  }

  function updateCountBadges() {
    var count = getWishlist().length;
    document.querySelectorAll('[data-wishlist-count]').forEach(function (el) {
      el.textContent = count;
      if (count > 0) {
        el.classList.remove('is-hidden');
      } else {
        el.classList.add('is-hidden');
      }
    });
  }

  /* ── Shared lists storage ─────────────────── */

  function getSharedLists() {
    try { return JSON.parse(localStorage.getItem(SHARED_LISTS_KEY)) || []; }
    catch (e) { return []; }
  }

  function saveSharedLists(lists) {
    safeSet(SHARED_LISTS_KEY, JSON.stringify(lists));
  }

  function addSharedList(entry) {
    var lists = getSharedLists();
    /* Replace if same listId exists */
    lists = lists.filter(function (l) { return l.listId !== entry.listId; });
    lists.unshift(entry);
    saveSharedLists(lists);
  }

  function removeSharedList(listId) {
    var lists = getSharedLists().filter(function (l) { return l.listId !== listId; });
    saveSharedLists(lists);
  }

  /* ── Save & Edit functions ───────────────────── */

  function saveList() {
    var items = getWishlist();
    if (items.length === 0) {
      showToast(CFG.saveEmptyToast || 'Voeg eerst producten toe aan je verlanglijstje');
      return;
    }
    var nameInput = document.querySelector('[data-save-name]');
    var name = nameInput ? nameInput.value.trim() : '';
    var typeRadio = document.querySelector('input[name="save-list-type"]:checked');
    var listType = typeRadio ? typeRadio.value : 'verjaardag';
    var listId = 'saved-' + Date.now().toString(36) + '-' + Math.random().toString(36).substr(2, 6);

    addSharedList({
      listId: listId,
      name: name,
      type: listType,
      sharedAt: null,
      encoded: null,
      products: items.map(function (item) {
        var handle = item.url ? item.url.replace(/.*\/products\//, '').split('?')[0] : '';
        return { id: String(item.id), handle: handle, title: item.title, image: item.image, price: item.price, url: item.url };
      }),
      status: 'draft'
    });

    saveWishlist([]);
    if (nameInput) nameInput.value = '';
    /* Close save form */
    var form = document.querySelector('[data-save-form]');
    if (form) form.style.display = 'none';
    showToast(CFG.savedToast || 'Lijstje opgeslagen!');
    renderWishlistPage();

    /* Highlight the newly saved card in sidebar */
    requestAnimationFrame(function () {
      var firstCard = document.querySelector('[data-saved-sidebar] .saved-list-card');
      if (firstCard) {
        firstCard.classList.add('saved-list-card--just-saved');
        firstCard.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
        firstCard.addEventListener('animationend', function () {
          firstCard.classList.remove('saved-list-card--just-saved');
        }, { once: true });
      }
    });
  }

  function showCopyListForm(listId, detailView) {
    var list = getSharedLists().filter(function (l) { return l.listId === listId; })[0];
    if (!list || !list.products) return;

    /* Remove existing copy form if any */
    var existing = detailView.querySelector('.copy-list-form');
    if (existing) { existing.remove(); return; }

    var defaultName = (list.name ? list.name + ' (kopie)' : '');
    var html =
      '<div class="copy-list-form">' +
        '<h3 class="copy-list-form__title">' + escHtml(CFG.copyFormTitle || 'Kopie opslaan') + '</h3>' +
        '<label class="copy-list-form__label">' + escHtml(CFG.copyFormNameLabel || 'Naam van het lijstje') + '</label>' +
        '<input type="text" class="copy-list-form__input" data-copy-name placeholder="' + escHtml(CFG.copyFormNamePlaceholder || 'Bijv. Lisa') + '" value="' + escHtml(defaultName) + '">' +
        '<div class="copy-list-form__type">' +
          '<label class="copy-list-form__radio"><input type="radio" name="copy-list-type" value="verjaardag"' + (list.type !== 'geboorte' ? ' checked' : '') + '> Verjaardagslijstje</label>' +
          '<label class="copy-list-form__radio"><input type="radio" name="copy-list-type" value="geboorte"' + (list.type === 'geboorte' ? ' checked' : '') + '> Geboortelijstje</label>' +
        '</div>' +
        '<div class="copy-list-form__actions">' +
          '<button class="btn btn--primary btn--sm" data-copy-save>' + escHtml(CFG.copyFormSaveBtn || 'Opslaan') + '</button>' +
          '<button class="btn btn--outline btn--sm" data-copy-cancel>' + escHtml(CFG.copyFormCancelBtn || 'Annuleren') + '</button>' +
        '</div>' +
      '</div>';

    var actionsEl = detailView.querySelector('.shared-detail__actions');
    if (actionsEl) {
      actionsEl.insertAdjacentHTML('beforebegin', html);
    } else {
      detailView.insertAdjacentHTML('beforeend', html);
    }

    var formEl = detailView.querySelector('.copy-list-form');
    var nameInput = formEl.querySelector('[data-copy-name]');
    nameInput.focus();
    nameInput.select();

    formEl.querySelector('[data-copy-save]').addEventListener('click', function () {
      var copyName = nameInput.value.trim();
      var typeRadio = formEl.querySelector('input[name="copy-list-type"]:checked');
      var copyType = typeRadio ? typeRadio.value : list.type;
      var newListId = 'saved-' + Date.now().toString(36) + '-' + Math.random().toString(36).substr(2, 6);

      addSharedList({
        listId: newListId,
        name: copyName,
        type: copyType,
        sharedAt: null,
        products: list.products.map(function (p) {
          return { id: String(p.id), handle: p.handle, title: p.title, image: p.image, price: p.price, url: p.url };
        }),
        status: 'draft'
      });

      formEl.remove();
      showToast(CFG.copyListSavedToast || 'Kopie opgeslagen!');
      renderWishlistPage();
    });

    formEl.querySelector('[data-copy-cancel]').addEventListener('click', function () {
      formEl.remove();
    });
  }

  function startEditList(listId) {
    var list = getSharedLists().filter(function (l) { return l.listId === listId; })[0];
    if (!list) return;

    setEditState({
      listId: listId,
      originalWishlist: getWishlist(),
      listName: list.name,
      listType: list.type
    });

    var items = list.products.map(function (p) {
      return { id: p.id, title: p.title, url: p.url, image: p.image, price: p.price, handle: p.handle, addedAt: Date.now() };
    });
    saveWishlist(items);

    var nameInput = document.querySelector('[data-birthday-name]');
    if (nameInput) nameInput.value = list.name || '';

    var detail = document.querySelector('[data-shared-detail]');
    if (detail) detail.style.display = 'none';
    var container = document.querySelector('[data-wishlist-page]');
    if (container) container.style.display = '';

    renderWishlistPage();
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }

  function saveEditedList() {
    var editState = getEditState();
    if (!editState) return;

    var items = getWishlist();
    var name = getBirthdayName();
    var lists = getSharedLists();
    var listIndex = -1;
    lists.forEach(function (l, i) { if (l.listId === editState.listId) listIndex = i; });
    if (listIndex === -1) { cancelEdit(); return; }

    var list = lists[listIndex];
    list.name = name || list.name;
    list.products = items.map(function (item) {
      var handle = item.url ? item.url.replace(/.*\/products\//, '').split('?')[0] : '';
      return { id: String(item.id), handle: handle, title: item.title, image: item.image, price: item.price, url: item.url };
    });

    lists[listIndex] = list;
    saveSharedLists(lists);

    /* Sync edits to server if list was shared via API */
    if (list.serverId) {
      apiFetch(CLAIMS_API_URL + '/api/lists/' + encodeURIComponent(list.serverId), {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: list.name,
          type: list.type,
          products: list.products,
          owner_id: getVisitorId()
        })
      }).catch(function () { /* best-effort sync */ });
    }

    saveWishlist(editState.originalWishlist || []);
    setEditState(null);
    showToast(CFG.editSavedToast || 'Lijstje bijgewerkt');
    renderWishlistPage();
  }

  function cancelEdit() {
    var editState = getEditState();
    if (!editState) return;
    saveWishlist(editState.originalWishlist || []);
    setEditState(null);
    renderWishlistPage();
  }

  function shareExistingList(listId) {
    var lists = getSharedLists();
    var list = null;
    var listIndex = -1;
    lists.forEach(function (l, i) { if (l.listId === listId) { list = l; listIndex = i; } });
    if (!list) return;

    /* If already shared via API, just re-open the modal */
    if (list.serverId) {
      var existingUrl = window.location.origin + '/pages/verlanglijstje?list=' + list.serverId;
      openReshareModal(existingUrl, list);
      return;
    }

    /* POST list to API */
    apiFetch(CLAIMS_API_URL + '/api/lists', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        name: list.name,
        type: list.type,
        products: list.products,
        owner_id: getVisitorId()
      })
    })
    .then(function (r) {
      if (!r.ok) throw new Error('share failed: ' + r.status);
      return r.json();
    })
    .then(function (data) {
      if (!data || !data.id) throw new Error('share failed: geen id in response');
      var serverId = data.id;
      var url = window.location.origin + '/pages/verlanglijstje?list=' + serverId;

      list.status = 'shared';
      list.sharedAt = Date.now();
      list.serverId = serverId;
      list.listId = serverId;
      lists[listIndex] = list;
      saveSharedLists(lists);
      markAsMyList(serverId);

      openReshareModal(url, list);
      renderWishlistPage();
    })
    .catch(function () {
      showToast('Kon lijstje niet delen, probeer het opnieuw');
    });
  }

  /* ── Wishlist page rendering ───────────────── */

  function renderWishlistPage() {
    var container = document.querySelector('[data-wishlist-page]');
    if (!container) return;

    var editState = getEditState();
    var items = getWishlist();
    var grid = container.querySelector('[data-wishlist-grid]');
    var empty = container.querySelector('[data-wishlist-empty]');
    var actions = container.querySelector('[data-wishlist-actions]');
    var countEl = container.querySelector('[data-wishlist-page-count]');
    var editBanner = document.querySelector('[data-edit-banner]');
    var saveSection = container.querySelector('[data-wishlist-save]');
    var sidebar = container.querySelector('[data-saved-sidebar]');
    var layout = container.querySelector('[data-wishlist-layout]');

    if (countEl) countEl.textContent = items.length + ' product' + (items.length !== 1 ? 'en' : '');

    /* Edit mode: show banner, hide save/share/sidebar */
    if (editBanner) {
      if (editState) {
        var typeLabel = editState.listType === 'geboorte' ? 'geboortelijstje' : 'verjaardagslijstje';
        var bannerTextEl = editBanner.querySelector('[data-edit-banner-text]');
        if (bannerTextEl) bannerTextEl.textContent = 'Je bewerkt: ' + (editState.listName || typeLabel);
        editBanner.style.display = '';
        if (actions) actions.style.display = 'none';
        if (saveSection) saveSection.style.display = 'none';
        if (sidebar) sidebar.style.display = 'none';
      } else {
        editBanner.style.display = 'none';
      }
    }

    if (items.length === 0) {
      if (grid) grid.style.display = 'none';
      if (empty) empty.style.display = '';
      if (!editState) {
        if (actions) actions.style.display = 'none';
        if (saveSection) saveSection.style.display = 'none';
      }
    } else {
      if (empty) empty.style.display = 'none';
      if (grid) {
        grid.style.display = '';
        renderOwnGrid(grid, items);
      }
      if (!editState) {
        if (actions) actions.style.display = '';
        if (saveSection) saveSection.style.display = '';
      }
    }

    /* Render sidebar with saved lists (hidden during edit) */
    if (!editState) {
      renderSavedListsSidebar();
    }

    /* Toggle layout class based on sidebar visibility */
    if (layout) {
      var hasSidebar = sidebar && sidebar.style.display !== 'none';
      layout.classList.toggle('wishlist-layout--has-sidebar', hasSidebar);
    }
  }

  function renderOwnGrid(grid, items) {
    var editState = getEditState();
    var html = '';
    items.forEach(function (item) {
      html += '<div class="wishlist-item">' +
        '<a href="' + escHtml(item.url) + '" class="wishlist-item__image">' +
          (item.image ? '<img src="' + escHtml(item.image) + '" alt="' + escHtml(item.title) + '" width="200" height="200" loading="lazy">' : '') +
        '</a>' +
        '<div class="wishlist-item__info">' +
          (item.vendor ? '<p class="wishlist-item__vendor">' + escHtml(item.vendor) + '</p>' : '') +
          '<a href="' + escHtml(item.url) + '" class="wishlist-item__title">' + escHtml(item.title) + '</a>' +
          '<p class="wishlist-item__price">' + escHtml(item.price) + '</p>' +
        '</div>' +
        '<div class="wishlist-item__actions">' +
          (!editState
            ? '<div class="wishlist-item__add-to-list">' +
                '<button class="btn btn--secondary btn--sm" data-add-to-list="' + escHtml(item.id) + '">' +
                  '<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></svg> ' +
                  escHtml(CFG.addToListText || 'Voeg toe aan lijst') +
                '</button>' +
              '</div>'
            : '') +
          '<a href="' + escHtml(item.url) + '" class="btn btn--primary btn--sm">' + escHtml(CFG.viewProductText || 'Bekijk product') + '</a>' +
          '<button class="wishlist-item__remove" data-wishlist-remove="' + escHtml(item.id) + '" aria-label="Verwijder ' + escHtml(item.title) + '">' +
            '<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><polyline points="3 6 5 6 21 6"/><path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"/></svg>' +
          '</button>' +
        '</div>' +
      '</div>';
    });
    grid.innerHTML = html;

    grid.querySelectorAll('[data-wishlist-remove]').forEach(function (btn) {
      btn.addEventListener('click', function () {
        removeFromWishlist(btn.dataset.wishlistRemove);
        renderWishlistPage();
      });
    });

    grid.querySelectorAll('[data-add-to-list]').forEach(function (btn) {
      btn.addEventListener('click', function (e) {
        e.stopPropagation();
        openListPicker(btn.dataset.addToList, btn);
      });
    });
  }

  /* ── List picker (add product to saved list) ── */

  function closeAllPickers() {
    document.querySelectorAll('.list-picker').forEach(function (p) { p.remove(); });
  }

  function openListPicker(productId, anchorBtn) {
    closeAllPickers();

    var lists = getSharedLists();
    var picker = document.createElement('div');
    picker.className = 'list-picker';

    var html = '';
    if (lists.length > 0) {
      html += '<p class="list-picker__title">' + escHtml(CFG.pickerTitle || 'Kies een lijstje') + '</p>';
      html += '<ul class="list-picker__list">';
      lists.forEach(function (list) {
        var isBirth = list.type === 'geboorte';
        var emoji = isBirth ? '\ud83c\udf7c' : '\ud83c\udf82';
        var label = (list.name || '') + ' ' + (isBirth ? 'geboortelijstje' : 'verjaardagslijstje');
        var alreadyIn = list.products.some(function (p) { return String(p.id) === String(productId); });
        html += '<li>' +
          '<button class="list-picker__option' + (alreadyIn ? ' list-picker__option--disabled' : '') + '"' +
            ' data-picker-list="' + escHtml(list.listId) + '"' +
            (alreadyIn ? ' disabled' : '') + '>' +
            '<span class="list-picker__emoji">' + emoji + '</span>' +
            '<span class="list-picker__label">' + escHtml(label) + '</span>' +
            (alreadyIn ? '<span class="list-picker__check">\u2713</span>' : '') +
          '</button>' +
        '</li>';
      });
      html += '</ul>';
      html += '<div class="list-picker__divider"></div>';
    }

    html += '<div class="list-picker__new">' +
      '<button class="list-picker__new-btn" data-picker-new>' +
        '<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></svg> ' +
        escHtml(CFG.pickerNewText || 'Nieuw lijstje aanmaken') +
      '</button>' +
      '<div class="list-picker__create" data-picker-create style="display:none">' +
        '<input type="text" placeholder="' + escHtml(CFG.namePlaceholder || 'Naam (bijv. Lisa)') + '" class="list-picker__input" data-picker-name>' +
        '<div class="list-picker__types">' +
          '<label class="list-picker__type"><input type="radio" name="picker-type" value="verjaardag" checked><span>\ud83c\udf82 Verjaardag</span></label>' +
          '<label class="list-picker__type"><input type="radio" name="picker-type" value="geboorte"><span>\ud83c\udf7c Geboorte</span></label>' +
        '</div>' +
        '<button class="btn btn--save btn--sm list-picker__confirm" data-picker-confirm>' + escHtml(CFG.pickerCreateConfirm || 'Aanmaken & toevoegen') + '</button>' +
      '</div>' +
    '</div>';

    picker.innerHTML = html;

    /* Position relative to the anchor button */
    var wrapper = anchorBtn.closest('.wishlist-item__add-to-list');
    if (wrapper) {
      wrapper.appendChild(picker);
    } else {
      anchorBtn.parentNode.appendChild(picker);
    }

    /* Bind list option clicks */
    picker.querySelectorAll('[data-picker-list]').forEach(function (btn) {
      btn.addEventListener('click', function () {
        addProductToSavedList(productId, btn.dataset.pickerList);
        closeAllPickers();
      });
    });

    /* Bind new list toggle */
    var newBtn = picker.querySelector('[data-picker-new]');
    var createForm = picker.querySelector('[data-picker-create]');
    if (newBtn && createForm) {
      newBtn.addEventListener('click', function () {
        newBtn.style.display = 'none';
        createForm.style.display = '';
        var nameInput = createForm.querySelector('[data-picker-name]');
        if (nameInput) nameInput.focus();
      });
    }

    /* Bind create confirm */
    var confirmBtn = picker.querySelector('[data-picker-confirm]');
    if (confirmBtn) {
      confirmBtn.addEventListener('click', function () {
        var nameInput = picker.querySelector('[data-picker-name]');
        var typeRadio = picker.querySelector('input[name="picker-type"]:checked');
        var name = nameInput ? nameInput.value.trim() : '';
        var type = typeRadio ? typeRadio.value : 'verjaardag';
        createListWithProduct(productId, name, type);
        closeAllPickers();
      });
    }

    /* Close on outside click */
    setTimeout(function () {
      document.addEventListener('click', function onOutside(e) {
        if (!picker.contains(e.target) && e.target !== anchorBtn && !anchorBtn.contains(e.target)) {
          closeAllPickers();
          document.removeEventListener('click', onOutside);
        }
      });
    }, 0);
  }

  function addProductToSavedList(productId, listId) {
    var lists = getSharedLists();
    var listIndex = -1;
    lists.forEach(function (l, i) { if (l.listId === listId) listIndex = i; });
    if (listIndex === -1) return;

    var item = getWishlist().filter(function (i) { return String(i.id) === String(productId); })[0];
    if (!item) return;

    var already = lists[listIndex].products.some(function (p) { return String(p.id) === String(productId); });
    if (already) return;

    var handle = item.url ? item.url.replace(/.*\/products\//, '').split('?')[0] : '';
    lists[listIndex].products.push({ id: String(item.id), handle: handle, title: item.title, image: item.image, price: item.price, url: item.url });

    /* Update encoded data if list was shared */
    if (lists[listIndex].encoded) {
      var list = lists[listIndex];
      var data = {
        i: list.products.map(function (p) { return { d: p.id, h: p.handle }; }),
        n: list.name,
        t: list.type
      };
      list.encoded = btoa(unescape(encodeURIComponent(JSON.stringify(data))));
    }

    saveSharedLists(lists);
    var listName = lists[listIndex].name || '';
    showToast(escHtml(item.title) + ' toegevoegd aan ' + escHtml(listName || 'het lijstje'));
    renderSavedListsSidebar();
  }

  function createListWithProduct(productId, name, type) {
    var item = getWishlist().filter(function (i) { return String(i.id) === String(productId); })[0];
    if (!item) return;

    var handle = item.url ? item.url.replace(/.*\/products\//, '').split('?')[0] : '';
    var listId = 'saved-' + Date.now().toString(36) + '-' + Math.random().toString(36).substr(2, 6);

    addSharedList({
      listId: listId,
      name: name,
      type: type || 'verjaardag',
      sharedAt: null,
      encoded: null,
      products: [{ id: String(item.id), handle: handle, title: item.title, image: item.image, price: item.price, url: item.url }],
      status: 'draft'
    });

    showToast('Lijstje aangemaakt met ' + escHtml(item.title));
    renderWishlistPage();

    /* Highlight new card */
    requestAnimationFrame(function () {
      var firstCard = document.querySelector('[data-saved-sidebar] .saved-list-card');
      if (firstCard) {
        firstCard.classList.add('saved-list-card--just-saved');
        firstCard.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
        firstCard.addEventListener('animationend', function () {
          firstCard.classList.remove('saved-list-card--just-saved');
        }, { once: true });
      }
    });
  }

  /* ── Saved lists sidebar ────────────────────── */

  function renderSavedListsSidebar() {
    var sidebar = document.querySelector('[data-saved-sidebar]');
    if (!sidebar) return;

    var lists = getSharedLists();
    if (lists.length === 0) {
      sidebar.style.display = 'none';
      return;
    }

    sidebar.style.display = '';
    var inner = sidebar.querySelector('[data-shared-lists-grid]');
    if (!inner) return;

    var html = '';
    lists.forEach(function (list) {
      var isBirth = list.type === 'geboorte';
      var typeEmoji = isBirth ? '\ud83c\udf7c' : '\ud83c\udf82';
      var typeLabel = isBirth ? 'Geboortelijstje' : 'Verjaardagslijstje';
      var statusLabel = list.products.length + ' product' + (list.products.length !== 1 ? 'en' : '');

      html += '<div class="saved-list-card" data-shared-list-id="' + escHtml(list.listId) + '">' +
        '<div class="saved-list-card__header">' +
          '<span class="saved-list-card__emoji">' + typeEmoji + '</span>' +
          '<div class="saved-list-card__meta">' +
            '<h4 class="saved-list-card__title">' + escHtml((list.name || '') + ' ' + typeLabel.toLowerCase()) + '</h4>' +
            '<p class="saved-list-card__count">' + statusLabel + '</p>' +
          '</div>' +
        '</div>' +
        '<div class="saved-list-card__actions">' +
          '<button class="btn btn--primary btn--sm" data-view-shared="' + escHtml(list.listId) + '">' +
            escHtml(CFG.viewListText || 'Bekijk') +
          '</button>' +
          '<button class="btn btn--secondary btn--sm" data-share-list="' + escHtml(list.listId) + '">' +
            escHtml(CFG.shareListText || 'Delen') +
          '</button>' +
        '</div>' +
      '</div>';
    });
    inner.innerHTML = html;

    /* Bind view buttons */
    inner.querySelectorAll('[data-view-shared]').forEach(function (btn) {
      btn.addEventListener('click', function () {
        openSharedListDetail(btn.dataset.viewShared);
      });
    });

    /* Bind share buttons (handles both draft and already-shared) */
    inner.querySelectorAll('[data-share-list]').forEach(function (btn) {
      btn.addEventListener('click', function () {
        var listId = btn.dataset.shareList;
        var list = getSharedLists().filter(function (l) { return l.listId === listId; })[0];
        if (!list) return;
        if (list.status === 'draft' || !list.encoded) {
          shareExistingList(listId);
        } else {
          var shareUrl = window.location.origin + '/pages/verlanglijstje?list=' + list.encoded;
          openReshareModal(shareUrl, list);
        }
      });
    });
  }

  /* ── Shared list detail view ────────────────── */

  function stopClaimsPoll() {
    if (_claimsPollTimer) { clearInterval(_claimsPollTimer); _claimsPollTimer = null; }
  }

  function openSharedListDetail(listId) {
    var list = getSharedLists().filter(function (l) { return l.listId === listId; })[0];
    if (!list) return;

    var container = document.querySelector('[data-wishlist-page]');
    var detailView = document.querySelector('[data-shared-detail]');
    if (!detailView) return;

    /* Persist detail view in URL hash so refresh re-opens it */
    history.replaceState(null, '', '#list=' + encodeURIComponent(listId));

    /* Hide main view, show detail */
    if (container) container.style.display = 'none';
    detailView.style.display = '';

    var isBirth = list.type === 'geboorte';
    var typeLabel = isBirth ? 'Geboortelijstje' : 'Verjaardagslijstje';
    var isDraft = list.status === 'draft';

    var titleEl = detailView.querySelector('[data-detail-title]');
    var subtitleEl = detailView.querySelector('[data-detail-subtitle]');
    var grid = detailView.querySelector('[data-detail-grid]');

    if (titleEl) titleEl.textContent = (list.name ? list.name + ' - ' : '') + typeLabel;
    if (subtitleEl) {
      if (isDraft || !list.sharedAt) {
        subtitleEl.textContent = list.products.length + ' product' + (list.products.length !== 1 ? 'en' : '') + ' - nog niet gedeeld';
      } else {
        var dateStr = new Date(list.sharedAt).toLocaleDateString('nl-NL', { day: 'numeric', month: 'long', year: 'numeric' });
        subtitleEl.textContent = list.products.length + ' product' + (list.products.length !== 1 ? 'en' : '') + ' - gedeeld op ' + dateStr;
      }
    }

    if (!grid) return;
    grid.innerHTML = '<div class="wishlist-loading">' + escHtml(CFG.loadingText || 'Laden...') + '</div>';

    /* Fetch claims from API */
    fetchClaims(listId).then(function (claims) {
      renderSharedListDetail(grid, list, claims, detailView);
    });

    /* Auto-refresh claims every 30s */
    stopClaimsPoll();
    _claimsPollTimer = setInterval(function () {
      fetchClaims(listId).then(function (freshClaims) {
        var currentList = getSharedLists().filter(function (l) { return l.listId === listId; })[0];
        if (currentList && detailView.style.display !== 'none') {
          renderSharedListDetail(grid, currentList, freshClaims, detailView);
        }
      });
    }, 30000);

    /* Clone-replace all action buttons to prevent duplicate listeners */
    function rebindBtn(selector, handler) {
      var btn = detailView.querySelector(selector);
      if (!btn) return;
      var fresh = btn.cloneNode(true);
      btn.parentNode.replaceChild(fresh, btn);
      fresh.addEventListener('click', handler);
    }

    /* Back button */
    rebindBtn('[data-detail-back]', function () {
      stopClaimsPoll();
      history.replaceState(null, '', window.location.pathname + window.location.search);
      detailView.style.display = 'none';
      if (container) container.style.display = '';
      renderWishlistPage();
    });

    /* Delete button */
    rebindBtn('[data-detail-delete]', function () {
      if (confirm(CFG.deleteListConfirm || 'Weet je zeker dat je dit lijstje wilt verwijderen?')) {
        stopClaimsPoll();
        history.replaceState(null, '', window.location.pathname + window.location.search);

        /* Delete from server if shared via API */
        if (list.serverId) {
          apiFetch(CLAIMS_API_URL + '/api/lists/' + encodeURIComponent(list.serverId) + '?owner_id=' + encodeURIComponent(getVisitorId()), {
            method: 'DELETE'
          }).catch(function () { /* best-effort */ });
        }

        removeSharedList(listId);
        detailView.style.display = 'none';
        if (container) container.style.display = '';
        renderWishlistPage();
        showToast(CFG.deleteListToast || 'Lijstje verwijderd');
      }
    });

    /* Share / reshare button */
    rebindBtn('[data-detail-reshare]', function () {
      if (isDraft || !list.encoded) {
        shareExistingList(listId);
      } else {
        var shareUrl = window.location.origin + '/pages/verlanglijstje?list=' + list.encoded;
        openReshareModal(shareUrl, list);
      }
    });

    /* Edit button */
    rebindBtn('[data-detail-edit]', function () {
      stopClaimsPoll();
      history.replaceState(null, '', window.location.pathname + window.location.search);
      startEditList(listId);
    });

    /* Copy list button - show inline form */
    rebindBtn('[data-detail-copy]', function () {
      showCopyListForm(listId, detailView);
    });
  }

  function renderSharedListDetail(grid, list, claims, detailView) {
    var isBirth = list.type === 'geboorte';
    var claimedIds = claims.map(function (c) { return typeof c === 'object' ? c.product_id : String(c); });
    var claimedCount = 0;

    list.products.forEach(function (p) {
      if (claimedIds.indexOf(String(p.id)) > -1) claimedCount++;
    });

    /* Update summary in subtitle */
    var summaryEl = detailView.querySelector('[data-detail-summary]');
    if (summaryEl) {
      if (claimedCount > 0) {
        var summaryText = tpl(CFG.birthSummary || '{count} van {total} cadeaus zijn al gereserveerd!', { count: claimedCount, total: list.products.length });
        summaryEl.textContent = summaryText;
        if (isBirth) {
          summaryEl.textContent += ' - ' + (CFG.birthSummaryHint || 'Welke dat zijn blijft een verrassing');
        }
        summaryEl.style.display = '';
      } else {
        summaryEl.textContent = 'Nog geen cadeaus gereserveerd';
        summaryEl.style.display = '';
      }
    }

    var html = '';
    list.products.forEach(function (item) {
      var claimed = claimedIds.indexOf(String(item.id)) > -1;
      /* For geboortelijst: don't reveal which specific items */
      var showTag = claimed && !isBirth;

      html += '<div class="wishlist-item' + (showTag ? ' wishlist-item--claimed' : '') + '">' +
        '<a href="' + escHtml(item.url || '/products/' + item.handle) + '" class="wishlist-item__image">' +
          (item.image ? '<img src="' + escHtml(item.image) + '" alt="' + escHtml(item.title) + '" width="200" height="200" loading="lazy">' : '') +
        '</a>' +
        '<div class="wishlist-item__info">' +
          '<a href="' + escHtml(item.url || '/products/' + item.handle) + '" class="wishlist-item__title">' + escHtml(item.title) + '</a>' +
          '<p class="wishlist-item__price">' + escHtml(item.price) + '</p>' +
        '</div>';

      if (showTag) {
        html += '<div class="wishlist-item__actions">' +
          '<span class="wishlist-item__reserved-tag">' +
            '<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="3"><polyline points="20 6 9 17 4 12"/></svg> ' +
            escHtml(CFG.senderClaimedTag || 'Iemand geeft dit cadeau') +
          '</span>' +
        '</div>';
      }

      html += '</div>';
    });
    grid.innerHTML = html;
  }

  /* ── Reshare modal (full share options) ──────── */

  function openReshareModal(url, list) {
    var isBirth = list.type === 'geboorte';
    var displayName = list.name || (isBirth ? 'de baby' : 'de jarige');
    var typeLabel = isBirth ? 'geboortelijstje' : 'verjaardagslijstje';
    var shareTitle = displayName ? displayName + "'s " + typeLabel + ' bij Poppedijn' : typeLabel.charAt(0).toUpperCase() + typeLabel.slice(1) + ' bij Poppedijn';
    var shareTextTemplate = isBirth
      ? (CFG.whatsappBirthText || 'Kijk eens naar het geboortelijstje van {naam} bij Poppedijn!')
      : (CFG.whatsappBirthdayText || "Kijk eens naar {naam}'s verlanglijstje bij Poppedijn!");
    var shareText = tpl(shareTextTemplate, { naam: displayName });

    var hasNativeShare = !!navigator.share;
    var whatsappMsg = encodeURIComponent(shareText + '\n\n' + url);
    var emailSubjectTpl = CFG.emailSubjectTemplate || "{naam}'s {type} bij Poppedijn";
    var emailSubject = encodeURIComponent(tpl(emailSubjectTpl, { naam: displayName, type: typeLabel }));
    var emailBodyTpl = CFG.emailBodyTemplate || 'Hoi!\n\n{tekst}\n\n{url}\n\nLiefs!';
    var emailBody = encodeURIComponent(tpl(emailBodyTpl, { naam: displayName, type: typeLabel, tekst: shareText, url: url }));
    var qrUrl = 'https://api.qrserver.com/v1/create-qr-code/?size=200x200&margin=8&data=' + encodeURIComponent(url);

    var existing = document.querySelector('.share-modal-overlay');
    if (existing) existing.remove();

    var html = '<div class="share-modal-overlay" data-share-overlay>' +
      '<div class="share-modal" role="dialog" aria-label="Deel verlanglijstje">' +
        '<button class="share-modal__close" data-share-close aria-label="Sluiten">' +
          '<svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>' +
        '</button>' +
        '<div class="share-modal__header">' +
          '<h3 class="share-modal__title">Deel ' + (isBirth ? 'het geboortelijstje' : escHtml(displayName) + "'s verlanglijstje") + '</h3>' +
          '<p class="share-modal__subtitle">' + escHtml(CFG.shareModalSubtitle || 'Kies hoe je het lijstje wilt versturen') + '</p>' +
        '</div>' +
        '<div class="share-modal__options">' +
          (hasNativeShare
            ? '<button class="share-option share-option--native" data-share-native>' +
                '<span class="share-option__icon">' +
                  '<svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M4 12v8a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2v-8"/><polyline points="16 6 12 2 8 6"/><line x1="12" y1="2" x2="12" y2="15"/></svg>' +
                '</span>' +
                '<span class="share-option__text">' +
                  '<span class="share-option__label">' + escHtml(CFG.shareNativeLabel || 'Deel via je telefoon') + '</span>' +
                  '<span class="share-option__hint">' + escHtml(CFG.shareNativeHint || 'WhatsApp, iMessage, en meer') + '</span>' +
                '</span>' +
              '</button>'
            : '') +
          '<a href="https://wa.me/?text=' + whatsappMsg + '" target="_blank" rel="noopener" class="share-option share-option--whatsapp">' +
            '<span class="share-option__icon">' +
              '<svg width="22" height="22" viewBox="0 0 24 24" fill="currentColor"><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 0 1-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 0 1-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 0 1 2.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0 0 12.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 0 0 5.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 0 0-3.48-8.413z"/></svg>' +
            '</span>' +
            '<span class="share-option__text">' +
              '<span class="share-option__label">' + escHtml(CFG.shareWhatsappLabel || 'WhatsApp') + '</span>' +
              '<span class="share-option__hint">' + escHtml(CFG.shareWhatsappHint || 'Stuur naar familie of een groep') + '</span>' +
            '</span>' +
          '</a>' +
          '<a href="mailto:?subject=' + emailSubject + '&body=' + emailBody + '" class="share-option share-option--email">' +
            '<span class="share-option__icon">' +
              '<svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><rect x="2" y="4" width="20" height="16" rx="2"/><path d="m22 7-8.97 5.7a1.94 1.94 0 0 1-2.06 0L2 7"/></svg>' +
            '</span>' +
            '<span class="share-option__text">' +
              '<span class="share-option__label">' + escHtml(CFG.shareEmailLabel || 'E-mail') + '</span>' +
              '<span class="share-option__hint">' + escHtml(CFG.shareEmailHint || 'Verstuur een mailtje met de link') + '</span>' +
            '</span>' +
          '</a>' +
          '<button class="share-option share-option--copy" data-share-copy>' +
            '<span class="share-option__icon">' +
              '<svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><rect x="9" y="9" width="13" height="13" rx="2" ry="2"/><path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"/></svg>' +
            '</span>' +
            '<span class="share-option__text">' +
              '<span class="share-option__label">' + escHtml(CFG.shareCopyLabel || 'Kopieer link') + '</span>' +
              '<span class="share-option__hint">' + escHtml(CFG.shareCopyHint || 'Plak de link waar je maar wilt') + '</span>' +
            '</span>' +
          '</button>' +
        '</div>' +
        '<details class="share-modal__qr">' +
          '<summary>' + escHtml(CFG.shareQrLabel || 'Of laat iemand de QR-code scannen') + '</summary>' +
          '<div class="share-modal__qr-code">' +
            '<img src="' + qrUrl + '" alt="QR-code voor verlanglijstje" width="200" height="200" loading="lazy">' +
            '<p class="share-modal__qr-hint">' + escHtml(CFG.shareQrHint || 'Richt de telefoon-camera op deze code') + '</p>' +
          '</div>' +
        '</details>' +
      '</div>' +
    '</div>';

    var wrapper = document.createElement('div');
    wrapper.innerHTML = html;
    var overlay = wrapper.firstChild;
    document.body.appendChild(overlay);
    requestAnimationFrame(function () { overlay.classList.add('is-visible'); });

    function closeModal() {
      overlay.classList.remove('is-visible');
      setTimeout(function () { overlay.remove(); }, 300);
    }

    overlay.querySelector('[data-share-close]').addEventListener('click', closeModal);
    overlay.addEventListener('click', function (e) { if (e.target === overlay) closeModal(); });
    document.addEventListener('keydown', function onEsc(e) {
      if (e.key === 'Escape') {
        closeModal();
        document.removeEventListener('keydown', onEsc);
      }
    });

    /* Native share */
    var nativeBtn = overlay.querySelector('[data-share-native]');
    if (nativeBtn) {
      nativeBtn.addEventListener('click', function () {
        navigator.share({
          title: shareTitle,
          text: shareText,
          url: url
        }).then(function () {
          closeModal();
          showToast('Bedankt voor het delen!');
        }).catch(function () { /* user cancelled */ });
      });
    }

    /* Copy link */
    var copyBtn = overlay.querySelector('[data-share-copy]');
    copyBtn.addEventListener('click', function () {
      copyToClipboard(url);
      var label = copyBtn.querySelector('.share-option__label');
      var hint = copyBtn.querySelector('.share-option__hint');
      label.textContent = CFG.shareCopiedLabel || 'Gekopieerd \u2713';
      hint.textContent = CFG.shareCopiedHint || 'De link staat op je klembord';
      copyBtn.classList.add('share-option--copied');
      setTimeout(function () {
        label.textContent = CFG.shareCopyLabel || 'Kopieer link';
        hint.textContent = CFG.shareCopyHint || 'Plak de link waar je maar wilt';
        copyBtn.classList.remove('share-option--copied');
      }, 3000);
    });
  }

  /* ── Birthday wishlist sharing ─────────────── */

  function getBirthdayName() {
    var input = document.querySelector('[data-birthday-name]');
    return input ? input.value.trim() : '';
  }

  function copyToClipboard(text) {
    if (navigator.clipboard) {
      return navigator.clipboard.writeText(text);
    } else {
      var ta = document.createElement('textarea');
      ta.value = text;
      ta.style.position = 'fixed';
      ta.style.left = '-9999px';
      document.body.appendChild(ta);
      ta.select();
      document.execCommand('copy');
      document.body.removeChild(ta);
      return Promise.resolve();
    }
  }

  /* ── Share modal ──────────────────────────── */

  function openShareModal(listType) {
    var items = getWishlist();
    if (items.length === 0) {
      showToast('Voeg eerst producten toe aan je verlanglijstje');
      return;
    }

    var name = getBirthdayName();
    var products = items.map(function (item) {
      var handle = item.url ? item.url.replace(/.*\/products\//, '').split('?')[0] : '';
      return { id: String(item.id), handle: handle, title: item.title, image: item.image, price: item.price, url: item.url };
    });

    /* POST list to API, then show modal */
    apiFetch(CLAIMS_API_URL + '/api/lists', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        name: name,
        type: listType || 'verjaardag',
        products: products,
        owner_id: getVisitorId()
      })
    })
    .then(function (r) {
      if (!r.ok) throw new Error('share failed: ' + r.status);
      return r.json();
    })
    .then(function (data) {
      if (!data || !data.id) throw new Error('share failed: geen id in response');
      var serverId = data.id;
      var url = window.location.origin + '/pages/verlanglijstje?list=' + serverId;

      markAsMyList(serverId);
      addSharedList({
        listId: serverId,
        serverId: serverId,
        name: name,
        type: listType,
        sharedAt: Date.now(),
        products: products,
        status: 'shared'
      });

      _showShareModalUI(url, name, listType);
      renderWishlistPage();
    })
    .catch(function () {
      showToast('Kon lijstje niet delen, probeer het opnieuw');
    });
  }

  function _showShareModalUI(url, name, listType) {
    var isBirth = listType === 'geboorte';
    var displayName = name || (isBirth ? 'de baby' : 'de jarige');
    var typeLabel = isBirth ? 'geboortelijstje' : 'verjaardagslijstje';
    var shareTitle = name ? name + "'s " + typeLabel + ' bij Poppedijn' : typeLabel.charAt(0).toUpperCase() + typeLabel.slice(1) + ' bij Poppedijn';
    var shareTextTemplate = isBirth
      ? (CFG.whatsappBirthText || 'Kijk eens naar het geboortelijstje van {naam} bij Poppedijn! Zo weet je precies welk cadeautje je kunt geven.')
      : (CFG.whatsappBirthdayText || "Kijk eens naar {naam}'s verlanglijstje bij Poppedijn! Zo weet je precies wat {naam} het allerliefste wil.");
    var shareText = tpl(shareTextTemplate, { naam: displayName });

    /* Remove existing modal */
    var existing = document.querySelector('.share-modal-overlay');
    if (existing) existing.remove();

    /* Build modal HTML */
    var hasNativeShare = !!navigator.share;
    var whatsappMsg = encodeURIComponent(shareText + '\n\n' + url);
    var emailSubjectTpl = CFG.emailSubjectTemplate || "{naam}'s {type} bij Poppedijn";
    var emailSubject = encodeURIComponent(tpl(emailSubjectTpl, { naam: displayName, type: typeLabel }));
    var emailBodyTpl = CFG.emailBodyTemplate || 'Hoi!\n\n{tekst}\n\n{url}\n\nLiefs!';
    var emailBody = encodeURIComponent(tpl(emailBodyTpl, { naam: displayName, type: typeLabel, tekst: shareText, url: url }));
    var qrUrl = 'https://api.qrserver.com/v1/create-qr-code/?size=200x200&margin=8&data=' + encodeURIComponent(url);

    var html = '<div class="share-modal-overlay" data-share-overlay>' +
      '<div class="share-modal" role="dialog" aria-label="Deel verlanglijstje">' +
        '<button class="share-modal__close" data-share-close aria-label="Sluiten">' +
          '<svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>' +
        '</button>' +
        '<div class="share-modal__header">' +
          '<h3 class="share-modal__title">Deel ' + (isBirth ? 'het geboortelijstje' : escHtml(displayName) + "'s verlanglijstje") + '</h3>' +
          '<p class="share-modal__subtitle">' + escHtml(CFG.shareModalSubtitle || 'Kies hoe je het lijstje wilt versturen') + '</p>' +
        '</div>' +
        '<div class="share-modal__options">' +
          (hasNativeShare
            ? '<button class="share-option share-option--native" data-share-native>' +
                '<span class="share-option__icon">' +
                  '<svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M4 12v8a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2v-8"/><polyline points="16 6 12 2 8 6"/><line x1="12" y1="2" x2="12" y2="15"/></svg>' +
                '</span>' +
                '<span class="share-option__text">' +
                  '<span class="share-option__label">' + escHtml(CFG.shareNativeLabel || 'Deel via je telefoon') + '</span>' +
                  '<span class="share-option__hint">' + escHtml(CFG.shareNativeHint || 'WhatsApp, iMessage, en meer') + '</span>' +
                '</span>' +
              '</button>'
            : '') +
          '<a href="https://wa.me/?text=' + whatsappMsg + '" target="_blank" rel="noopener" class="share-option share-option--whatsapp">' +
            '<span class="share-option__icon">' +
              '<svg width="22" height="22" viewBox="0 0 24 24" fill="currentColor"><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 0 1-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 0 1-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 0 1 2.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0 0 12.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 0 0 5.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 0 0-3.48-8.413z"/></svg>' +
            '</span>' +
            '<span class="share-option__text">' +
              '<span class="share-option__label">' + escHtml(CFG.shareWhatsappLabel || 'WhatsApp') + '</span>' +
              '<span class="share-option__hint">' + escHtml(CFG.shareWhatsappHint || 'Stuur naar familie of een groep') + '</span>' +
            '</span>' +
          '</a>' +
          '<a href="mailto:?subject=' + emailSubject + '&body=' + emailBody + '" class="share-option share-option--email">' +
            '<span class="share-option__icon">' +
              '<svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><rect x="2" y="4" width="20" height="16" rx="2"/><path d="m22 7-8.97 5.7a1.94 1.94 0 0 1-2.06 0L2 7"/></svg>' +
            '</span>' +
            '<span class="share-option__text">' +
              '<span class="share-option__label">' + escHtml(CFG.shareEmailLabel || 'E-mail') + '</span>' +
              '<span class="share-option__hint">' + escHtml(CFG.shareEmailHint || 'Verstuur een mailtje met de link') + '</span>' +
            '</span>' +
          '</a>' +
          '<button class="share-option share-option--copy" data-share-copy>' +
            '<span class="share-option__icon">' +
              '<svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><rect x="9" y="9" width="13" height="13" rx="2" ry="2"/><path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"/></svg>' +
            '</span>' +
            '<span class="share-option__text">' +
              '<span class="share-option__label">' + escHtml(CFG.shareCopyLabel || 'Kopieer link') + '</span>' +
              '<span class="share-option__hint">' + escHtml(CFG.shareCopyHint || 'Plak de link waar je maar wilt') + '</span>' +
            '</span>' +
          '</button>' +
        '</div>' +
        '<details class="share-modal__qr">' +
          '<summary>' + escHtml(CFG.shareQrLabel || 'Of laat iemand de QR-code scannen') + '</summary>' +
          '<div class="share-modal__qr-code">' +
            '<img src="' + qrUrl + '" alt="QR-code voor verlanglijstje" width="200" height="200" loading="lazy">' +
            '<p class="share-modal__qr-hint">' + escHtml(CFG.shareQrHint || 'Richt de telefoon-camera op deze code') + '</p>' +
          '</div>' +
        '</details>' +
      '</div>' +
    '</div>';

    /* Insert and animate in */
    var wrapper = document.createElement('div');
    wrapper.innerHTML = html;
    var overlay = wrapper.firstChild;
    document.body.appendChild(overlay);

    requestAnimationFrame(function () {
      overlay.classList.add('is-visible');
    });

    /* Bind close */
    function closeModal() {
      overlay.classList.remove('is-visible');
      setTimeout(function () { overlay.remove(); }, 300);
    }

    overlay.querySelector('[data-share-close]').addEventListener('click', closeModal);
    overlay.addEventListener('click', function (e) {
      if (e.target === overlay) closeModal();
    });
    document.addEventListener('keydown', function onEsc(e) {
      if (e.key === 'Escape') {
        closeModal();
        document.removeEventListener('keydown', onEsc);
      }
    });

    /* Native share */
    var nativeBtn = overlay.querySelector('[data-share-native]');
    if (nativeBtn) {
      nativeBtn.addEventListener('click', function () {
        navigator.share({
          title: shareTitle,
          text: shareText,
          url: url
        }).then(function () {
          closeModal();
          showToast('Bedankt voor het delen!');
        }).catch(function () { /* user cancelled */ });
      });
    }

    /* Copy link */
    var copyBtn = overlay.querySelector('[data-share-copy]');
    copyBtn.addEventListener('click', function () {
      copyToClipboard(url);
      var label = copyBtn.querySelector('.share-option__label');
      var hint = copyBtn.querySelector('.share-option__hint');
      label.textContent = CFG.shareCopiedLabel || 'Gekopieerd \u2713';
      hint.textContent = CFG.shareCopiedHint || 'De link staat op je klembord';
      copyBtn.classList.add('share-option--copied');
      setTimeout(function () {
        label.textContent = CFG.shareCopyLabel || 'Kopieer link';
        hint.textContent = CFG.shareCopyHint || 'Plak de link waar je maar wilt';
        copyBtn.classList.remove('share-option--copied');
      }, 3000);
    });
  }

  /* ── Shared wishlist viewer ────────────────── */

  var CLAIMS_KEY = 'poppedijn_wishlist_claims';

  function isSharedStorageEnabled() {
    return !!CLAIMS_API_URL;
  }

  /* Local claims (fallback) */
  function getClaimsStore() {
    try { return JSON.parse(localStorage.getItem(CLAIMS_KEY)) || {}; }
    catch (e) { return {}; }
  }

  function getLocalClaims(listId) {
    var store = getClaimsStore();
    return store[listId] || [];
  }

  function setLocalClaim(listId, productId, add) {
    var store = getClaimsStore();
    if (!store[listId]) store[listId] = [];
    var idx = store[listId].indexOf(String(productId));
    if (add && idx === -1) {
      store[listId].push(String(productId));
    } else if (!add && idx > -1) {
      store[listId].splice(idx, 1);
    }
    safeSet(CLAIMS_KEY, JSON.stringify(store));
  }

  /* Shared claims (Poppedijn API) - returns [{product_id, claimer_id}] */
  function fetchClaims(listId) {
    if (!isSharedStorageEnabled()) {
      return Promise.resolve(getLocalClaims(listId).map(function (id) {
        return { product_id: id, claimer_id: null };
      }));
    }
    return apiFetch(CLAIMS_API_URL + '/api/claims/' + encodeURIComponent(listId))
      .then(function (r) { return r.json(); })
      .then(function (data) {
        if (!Array.isArray(data)) return [];
        return data;
      })
      .catch(function () { return []; });
  }

  function addClaim(listId, productId) {
    var visitorId = getVisitorId();
    setLocalClaim(listId, productId, true);
    if (!isSharedStorageEnabled()) return Promise.resolve(true);
    return apiFetch(CLAIMS_API_URL + '/api/claims', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ list_id: listId, product_id: String(productId), claimer_id: visitorId })
    }).then(function () { return true; }).catch(function () { return true; });
  }

  function removeClaim(listId, productId) {
    var visitorId = getVisitorId();
    setLocalClaim(listId, productId, false);
    if (!isSharedStorageEnabled()) return Promise.resolve(false);
    return apiFetch(CLAIMS_API_URL + '/api/claims/' + encodeURIComponent(listId) + '/' + encodeURIComponent(String(productId)) + '?claimer_id=' + encodeURIComponent(visitorId), {
      method: 'DELETE'
    }).then(function (r) {
      if (r.status === 403) {
        /* Not our claim - re-add to local */
        setLocalClaim(listId, productId, true);
        return true;
      }
      return false;
    }).catch(function () { return false; });
  }

  /* Sender identity tracking */
  function markAsMyList(listId) {
    var lists = getMyLists();
    if (lists.indexOf(listId) === -1) {
      lists.push(listId);
      safeSet(MY_LISTS_KEY, JSON.stringify(lists));
    }
  }

  function getMyLists() {
    try { return JSON.parse(localStorage.getItem(MY_LISTS_KEY)) || []; }
    catch (e) { return []; }
  }

  function isMyList(listId) {
    return getMyLists().indexOf(listId) > -1;
  }

  function addToCartByHandle(handle) {
    fetch('/products/' + handle + '.js')
      .then(function (r) { return r.json(); })
      .then(function (product) {
        var variantId = product.variants && product.variants[0] ? product.variants[0].id : null;
        if (!variantId) return;
        /* Check if already in cart to prevent duplicates */
        return fetch('/cart.js').then(function (r) { return r.json(); }).then(function (cart) {
          var inCart = cart.items && cart.items.some(function (ci) { return ci.variant_id === variantId; });
          if (inCart) return; /* Already in cart */
          return fetch('/cart/add.js', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ items: [{ id: variantId, quantity: 1 }] })
          });
        });
      })
      .then(function () {
        document.dispatchEvent(new CustomEvent('cart:updated'));
        document.dispatchEvent(new CustomEvent('cart:open'));
      })
      .catch(function () {});
  }

  function removeFromCartByHandle(handle) {
    fetch('/products/' + handle + '.js')
      .then(function (r) { return r.json(); })
      .then(function (product) {
        var variantId = product.variants && product.variants[0] ? product.variants[0].id : null;
        if (!variantId) return;
        return fetch('/cart/change.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ id: variantId, quantity: 0 })
        });
      })
      .then(function () {
        document.dispatchEvent(new CustomEvent('cart:updated'));
      })
      .catch(function () {});
  }

  function renderSharedWishlist() {
    var viewer = document.querySelector('[data-shared-wishlist]');
    if (!viewer) return;

    var params = new URLSearchParams(window.location.search);
    var listParam = params.get('list');
    if (!listParam) return;

    var nameEl = viewer.querySelector('[data-shared-name]');
    var grid = viewer.querySelector('[data-shared-grid]');

    /* Short ID (≤12 chars) = API lookup; longer = legacy base64 */
    if (listParam.length <= 12) {
      var listId = listParam;
      var sender = isMyList(listId);

      if (grid) grid.innerHTML = '<div class="wishlist-loading">' + escHtml(CFG.loadingText || 'Verlanglijstje laden...') + '</div>';

      apiFetch(CLAIMS_API_URL + '/api/lists/' + encodeURIComponent(listId))
        .then(function (r) {
          if (!r.ok) throw new Error('not found');
          return r.json();
        })
        .then(function (listData) {
          var name = listData.name;
          var listType = listData.type || 'verjaardag';
          var isBirth = listType === 'geboorte';
          var items = (listData.products || []).map(function (p) {
            return {
              id: String(p.id),
              handle: p.handle || '',
              title: p.title,
              url: p.url || '/products/' + (p.handle || ''),
              image: p.image || '',
              price: p.price || '',
              vendor: p.vendor || ''
            };
          });

          if (nameEl) {
            var typeEmoji = isBirth ? '\ud83c\udf7c ' : '\ud83c\udf82 ';
            var typeLabel = isBirth ? 'Geboortelijstje' : 'Verjaardagslijstje';
            nameEl.textContent = name ? name + "'s " + typeLabel.toLowerCase() : typeLabel;
            nameEl.insertAdjacentHTML('beforeend', ' <span class="shared-type-badge shared-type-badge--' + listType + '">' + typeEmoji + typeLabel + '</span>');
          }

          if (!grid) return;

          fetchClaims(listId).then(function (claims) {
            renderSharedGrid(grid, items, listId, listType, name, claims, sender);
          });
        })
        .catch(function () {
          if (nameEl) nameEl.textContent = 'Dit verlanglijstje is niet meer beschikbaar';
          if (grid) grid.innerHTML = '<p class="wishlist-empty-message">Het lijstje is verwijderd of de link is ongeldig.</p>';
        });
    } else {
      /* Legacy base64 format - backward compatibility */
      var encoded = listParam;
      var legacyListId = encoded.substring(0, 32);
      var sender = isMyList(legacyListId);

      try {
        var raw = JSON.parse(decodeURIComponent(escape(atob(encoded))));
        var isNewFormat = Array.isArray(raw.i);
        var name = isNewFormat ? raw.n : raw.name;
        var listType = raw.t || 'verjaardag';
        var isBirth = listType === 'geboorte';

        if (nameEl) {
          var typeEmoji = isBirth ? '\ud83c\udf7c ' : '\ud83c\udf82 ';
          var typeLabel = isBirth ? 'Geboortelijstje' : 'Verjaardagslijstje';
          nameEl.textContent = name ? name + "'s " + typeLabel.toLowerCase() : typeLabel;
          nameEl.insertAdjacentHTML('beforeend', ' <span class="shared-type-badge shared-type-badge--' + listType + '">' + typeEmoji + typeLabel + '</span>');
        }

        if (!grid) return;

        if (isNewFormat) {
          grid.innerHTML = '<div class="wishlist-loading">' + escHtml(CFG.loadingText || 'Verlanglijstje laden...') + '</div>';
          var productFetches = raw.i.map(function (item) {
            return fetch('/products/' + item.h + '.js')
              .then(function (r) { return r.json(); })
              .then(function (p) {
                return {
                  id: item.d,
                  handle: item.h,
                  title: p.title,
                  url: '/products/' + item.h,
                  image: p.images && p.images[0] ? p.images[0] : '',
                  price: '\u20ac' + (p.price / 100).toFixed(2).replace('.', ','),
                  vendor: p.vendor
                };
              })
              .catch(function () { return null; });
          });

          Promise.all([Promise.all(productFetches), fetchClaims(legacyListId)]).then(function (results) {
            var validItems = results[0].filter(function (i) { return i !== null; });
            var claims = results[1];
            renderSharedGrid(grid, validItems, legacyListId, listType, name, claims, sender);
          });
        } else {
          var items = (raw.items || []).map(function (item) {
            return {
              id: String(item.id),
              handle: item.url ? item.url.replace(/.*\/products\//, '').split('?')[0] : '',
              title: item.title,
              url: item.url,
              image: item.image,
              price: item.price,
              vendor: item.vendor
            };
          });
          fetchClaims(legacyListId).then(function (claims) {
            renderSharedGrid(grid, items, legacyListId, listType, name, claims, sender);
          });
        }
      } catch (e) {
        /* Silently ignore invalid wishlist data */
      }
    }
  }

  function renderSharedGrid(grid, items, listId, listType, listName, claims, sender) {
    var isBirth = listType === 'geboorte';
    var visitorId = getVisitorId();
    var claimedIds = claims.map(function (c) { return typeof c === 'object' ? c.product_id : String(c); });
    var claimedCount = claimedIds.length;
    var html = '';

    /* Sender banner */
    if (sender) {
      html += '<div class="shared-sender-banner">' +
        '<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z"/></svg> ' +
        escHtml(CFG.senderBanner || 'Dit is jouw lijstje') +
      '</div>';
    }

    /* For geboortelijst sender: show only a summary, no individual claims */
    if (isBirth && sender && claimedCount > 0) {
      var summaryText = tpl(CFG.birthSummary || '{count} van {total} cadeaus zijn al gereserveerd!', { count: claimedCount, total: items.length });
      html += '<div class="shared-claims-summary">' +
        '<p class="shared-claims-summary__count">' + escHtml(summaryText) + '</p>' +
        '<p class="shared-claims-summary__hint">' + escHtml(CFG.birthSummaryHint || 'Welke dat zijn blijft een verrassing') + '</p>' +
      '</div>';
    }

    items.forEach(function (item) {
      var claimObj = null;
      claims.forEach(function (c) {
        if ((typeof c === 'object' ? c.product_id : String(c)) === String(item.id)) claimObj = c;
      });
      var claimed = !!claimObj;
      var isMyClaim = claimed && claimObj && claimObj.claimer_id === visitorId;
      var isOtherClaim = claimed && !isMyClaim;
      /* For geboortelijst sender: hide individual claim state */
      var showClaimed = claimed && !(isBirth && sender);

      html += '<div class="wishlist-item' + (showClaimed ? ' wishlist-item--claimed' : '') + '">' +
      '<a href="' + escHtml(item.url) + '" class="wishlist-item__image">' +
        (item.image ? '<img src="' + escHtml(item.image) + '" alt="' + escHtml(item.title) + '" width="200" height="200" loading="lazy">' : '') +
      '</a>' +
      '<div class="wishlist-item__info">' +
        (item.vendor ? '<p class="wishlist-item__vendor">' + escHtml(item.vendor) + '</p>' : '') +
        '<a href="' + escHtml(item.url) + '" class="wishlist-item__title">' + escHtml(item.title) + '</a>' +
        '<p class="wishlist-item__price">' + escHtml(item.price) + '</p>' +
      '</div>';

      /* Sender sees no claim buttons; receiver sees claim UI */
      if (!sender) {
        html += '<div class="wishlist-item__actions">';

        if (isOtherClaim) {
          /* Claimed by someone else - blocked, not clickable */
          html += '<span class="btn btn--claimed btn--sm wishlist-claim-btn wishlist-claim-btn--blocked">' +
            '<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="3"><polyline points="20 6 9 17 4 12"/></svg> ' +
            escHtml(CFG.claimedButtonText || 'Gereserveerd') +
          '</span>';
        } else if (isMyClaim) {
          /* My own claim - can unclaim */
          html += '<button type="button" class="btn btn--claimed btn--sm wishlist-claim-btn wishlist-claim-btn--mine" data-claim-product="' + escHtml(item.id) + '" data-claim-handle="' + escHtml(item.handle) + '">' +
            '<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="3"><polyline points="20 6 9 17 4 12"/></svg> ' +
            escHtml(CFG.myClaimText || 'Jouw reservering') +
          '</button>';
        } else {
          /* Not claimed - available */
          html += '<button type="button" class="btn btn--accent btn--sm wishlist-claim-btn" data-claim-product="' + escHtml(item.id) + '" data-claim-handle="' + escHtml(item.handle) + '">' +
            '<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z"/></svg> ' +
            escHtml(CFG.claimButtonText || 'Ik geef dit cadeau') +
          '</button>';
        }

        html += '<a href="' + escHtml(item.url) + '" class="btn btn--secondary btn--sm">' + escHtml(CFG.viewProductText || 'Bekijk product') + '</a>' +
        '</div>';
      } else if (!isBirth && claimed) {
        /* Verjaardagslijst sender: show reservation indicator (no buttons) */
        html += '<div class="wishlist-item__actions">' +
          '<span class="wishlist-item__reserved-tag">' +
            '<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="3"><polyline points="20 6 9 17 4 12"/></svg> ' + escHtml(CFG.senderClaimedTag || 'Iemand geeft dit cadeau') +
          '</span>' +
        '</div>';
      }

      html += '</div>';
    });
    grid.innerHTML = html;

    /* Bind claim buttons (only for receivers) */
    if (!sender) {
      grid.querySelectorAll('[data-claim-product]').forEach(function (btn) {
        btn.addEventListener('click', function () {
          /* Race-condition guard: voorkom dubbele submits bij snel klikken of multi-tab */
          if (btn.dataset.claimPending === '1') return;
          btn.dataset.claimPending = '1';
          btn.disabled = true;

          function release() {
            delete btn.dataset.claimPending;
            btn.disabled = false;
          }

          var productId = btn.dataset.claimProduct;
          var handle = btn.dataset.claimHandle;
          var claimObj = null;
          claims.forEach(function (c) {
            if ((typeof c === 'object' ? c.product_id : String(c)) === String(productId)) claimObj = c;
          });
          var isMine = claimObj && claimObj.claimer_id === visitorId;

          if (isMine) {
            /* Unclaim + remove from cart */
            removeClaim(listId, productId).then(function () {
              removeFromCartByHandle(handle);
              fetchClaims(listId).then(function (freshClaims) {
                renderSharedGrid(grid, items, listId, listType, listName, freshClaims, sender);
                release();
              });
            }).catch(release);
          } else if (!claimObj) {
            /* New claim + add to cart */
            addClaim(listId, productId).then(function () {
              addToCartByHandle(handle);
              var toastMsg = isBirth
                ? (CFG.birthClaimedToast || 'Gereserveerd! Het blijft een verrassing')
                : (CFG.claimedToast || 'Gereserveerd!');
              showToast(toastMsg);
              fetchClaims(listId).then(function (freshClaims) {
                renderSharedGrid(grid, items, listId, listType, listName, freshClaims, sender);
                release();
              });
            }).catch(function () {
              showToast('Reserveren mislukt. Iemand anders was je voor of je internet hapert.');
              release();
            });
          } else {
            /* claimed by someone else - geen actie, gewoon release */
            release();
          }
        });
      });
    }
  }

  /* ── Quick add to cart ───────────────────── */

  function handleQuickAdd(variantId, btn) {
    btn.disabled = true;
    btn.classList.add('is-loading');

    fetch('/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ items: [{ id: parseInt(variantId, 10), quantity: 1 }] })
    })
    .then(function (r) { return r.json(); })
    .then(function () {
      btn.classList.remove('is-loading');
      btn.classList.add('is-added');
      var span = btn.querySelector('span');
      if (span) span.textContent = 'Toegevoegd ✓';

      document.dispatchEvent(new CustomEvent('cart:updated'));
      document.dispatchEvent(new CustomEvent('cart:open'));

      setTimeout(function () {
        btn.classList.remove('is-added');
        btn.disabled = false;
        if (span) span.textContent = 'Toevoegen';
      }, 2000);
    })
    .catch(function () {
      btn.classList.remove('is-loading');
      btn.disabled = false;
    });
  }

  /* ── Event listeners ───────────────────────── */

  document.addEventListener('click', function (e) {
    /* Wishlist toggle */
    var wishlistBtn = e.target.closest('[data-wishlist-toggle]');
    if (wishlistBtn) {
      e.preventDefault();
      var id = wishlistBtn.dataset.wishlistToggle;
      var card = wishlistBtn.closest('.product-card');
      var page = wishlistBtn.closest('.product-page');

      var productData = {};
      if (card) {
        var link = card.querySelector('.product-card__link');
        var img = card.querySelector('.product-card__image img') || card.querySelector('.product-card__image');
        productData = {
          id: id,
          title: card.querySelector('.product-card__title') ? card.querySelector('.product-card__title').textContent : '',
          url: link ? link.getAttribute('href') : '',
          image: img ? img.src : '',
          price: card.querySelector('.product-card__price span') ? card.querySelector('.product-card__price span').textContent : '',
          vendor: card.querySelector('.product-card__vendor') ? card.querySelector('.product-card__vendor').textContent : ''
        };
      } else if (page) {
        productData = {
          id: id,
          title: page.dataset.productTitle || '',
          url: page.dataset.productUrl || '',
          image: page.dataset.productImage || '',
          price: page.dataset.productPrice || '',
          vendor: page.dataset.productVendor || ''
        };
      }

      toggleWishlist(id, productData);
      return;
    }

    /* Quick add to cart */
    var quickAdd = e.target.closest('[data-quick-add]');
    if (quickAdd) {
      e.preventDefault();
      e.stopPropagation();
      handleQuickAdd(quickAdd.dataset.quickAdd, quickAdd);
      return;
    }

    /* Save trigger: toggle inline form */
    var saveTrigger = e.target.closest('[data-save-trigger]');
    if (saveTrigger) {
      e.preventDefault();
      var form = document.querySelector('[data-save-form]');
      var shareSection = document.querySelector('[data-wishlist-actions]');
      if (form) {
        var opening = form.style.display === 'none';
        form.style.display = opening ? '' : 'none';
        if (shareSection) {
          if (opening) {
            shareSection.classList.add('wishlist-fade-out');
          } else {
            shareSection.classList.remove('wishlist-fade-out');
          }
        }
      }
      return;
    }

    /* Save confirm: save list */
    var saveConfirm = e.target.closest('[data-save-confirm]');
    if (saveConfirm) {
      e.preventDefault();
      saveList();
      return;
    }

    /* Save cancel: close form */
    var saveCancel = e.target.closest('[data-save-cancel]');
    if (saveCancel) {
      e.preventDefault();
      var formEl = document.querySelector('[data-save-form]');
      if (formEl) formEl.style.display = 'none';
      var shareSec = document.querySelector('[data-wishlist-actions]');
      if (shareSec) shareSec.classList.remove('wishlist-fade-out');
      return;
    }

    /* Edit save */
    var editSaveBtn = e.target.closest('[data-edit-save]');
    if (editSaveBtn) {
      e.preventDefault();
      saveEditedList();
      return;
    }

    /* Edit cancel */
    var editCancelBtn = e.target.closest('[data-edit-cancel]');
    if (editCancelBtn) {
      e.preventDefault();
      cancelEdit();
      return;
    }

    /* Share wishlist */
    var shareBtn = e.target.closest('[data-wishlist-share]');
    if (shareBtn) {
      e.preventDefault();
      var listType = shareBtn.dataset.listType || 'verjaardag';
      openShareModal(listType);
      return;
    }
  });

  /* ── Init ───────────────────────────────────── */

  updateAllButtons();
  updateCountBadges();
  renderWishlistPage();
  renderSharedWishlist();

  /* Restore detail view if URL has #list= hash */
  (function () {
    var hash = window.location.hash;
    if (hash && hash.indexOf('#list=') === 0) {
      var storedListId = decodeURIComponent(hash.substring(6));
      if (storedListId) openSharedListDetail(storedListId);
    }
  })();

})();
